import {HttpClient} from '@/model/http';
import {isLeft} from 'fp-ts/lib/Either';

export type DescriptionSourceType = 'text' | 'audio' | 'image';

export type DescriptionSourceState = 'processing' | 'completed' | 'error';

export interface DescriptionSource {
    id: string;

    type: DescriptionSourceType;

    /** What the user named the source by, e.g. "1. NP", the AI gets it as a hint too */
    label: string;

    /** Name of the uploaded file, null for an attachment of the estimate */
    fileName: string | null;

    /** Attachment of the estimate the source reads */
    attachmentId: string | null;

    /** Absolute URL of the file, null when the attachment was removed from the estimate */
    url: string | null;

    /** Null for a source added before the states were kept */
    state: DescriptionSourceState | null;

    /** Number of parameters found, null until the parameters are extracted */
    parameterCount: number | null;

    position: number;
}

export interface DescriptionSources {
    /** In the order the user added them or moved them to */
    sources: DescriptionSource[];

    creditBalance: number;

    /** Credits the processing of one source costs */
    creditCost: number;
}

export type NewDescriptionSource =
    | {kind: 'file', label: string, file: File}
    | {kind: 'text', label: string, text: string}
    | {kind: 'attachment', label: string, attachmentId: string};

/**
 * The sources of an expert estimate the AI extracts the parameters of its form from
 */
export default class EstimateDescriptionSources {
    private static readonly HEADERS = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    };

    constructor(
        private readonly estimateId: string,
        private readonly http: HttpClient,
    ) {
    }

    public async load(): Promise<DescriptionSources> {
        return this.request(this.url(), {method: 'GET', headers: {'Accept': 'application/json'}});
    }

    /**
     * Adds the sources in the given order and starts their processing
     */
    public async add(sources: NewDescriptionSource[]): Promise<DescriptionSources> {
        const formData = new FormData();
        const files: File[] = [];

        const items = sources.map(source => {
            if (source.kind === 'file') {
                files.push(source.file);

                return {label: source.label, file: files.length - 1};
            }

            return source.kind === 'text'
                ? {label: source.label, text: source.text}
                : {label: source.label, attachmentId: source.attachmentId};
        });

        formData.append('sources', JSON.stringify(items));
        files.forEach(file => formData.append('files[]', file));

        return this.request(this.url(), {method: 'POST', body: formData});
    }

    public async remove(sourceId: string): Promise<DescriptionSources> {
        return this.request(this.url(sourceId), {method: 'DELETE', headers: EstimateDescriptionSources.HEADERS});
    }

    public async move(sourceId: string, position: number): Promise<DescriptionSources> {
        return this.request(this.url(sourceId) + '/move', {
            method: 'PUT',
            headers: EstimateDescriptionSources.HEADERS,
            body: JSON.stringify({position}),
        });
    }

    /**
     * Processes again a source whose processing failed
     */
    public async retry(sourceId: string): Promise<DescriptionSources> {
        return this.request(this.url(sourceId) + '/retry', {method: 'POST', headers: EstimateDescriptionSources.HEADERS});
    }

    private url(sourceId?: string): string {
        const url = `/api/expert-estimate/estimates/${this.estimateId}/description-sources`;

        return sourceId === undefined ? url : `${url}/${sourceId}`;
    }

    private async request(url: string, init: RequestInit): Promise<DescriptionSources> {
        const response = await this.http.safeRequest<DescriptionSources>(url, init);

        if (isLeft(response)) {
            throw response.left;
        }

        return response.right;
    }
}
